import { debounce } from 'lodash';
import { useMemo, useState, useEffect } from 'react';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { useGetAccountsQuery } from 'src/services/organisation/organisation.service';

// ----------------------------------------------------------------------

export interface OwnerOption {
  id: string;
  name: string;
}

/** Admins and operational admins can open and fill their team's call lists (JT1-1827). */
export function useIsCallListAdmin(): boolean {
  const authContext = useAuthContext();
  return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext?.account?.org_role as AccountRole);
}

type Props = {
  /** The chosen team member's account id; empty means the admin's own lists. */
  value: string | null;
  onChange: (ownerId: string | null) => void;
  label?: string;
  size?: 'small' | 'medium';
};

/** Pick whose call lists to work with: "Me" or a team member. Render only for admins. */
export default function CallListOwnerPicker({ value, onChange, label, size = 'small' }: Props) {
  const { t } = useTranslate();
  const authContext = useAuthContext();
  const [search, setSearch] = useState('');

  const onSearch = useMemo(() => debounce((text: string) => setSearch(text), 400), []);
  useEffect(() => () => onSearch.cancel(), [onSearch]);

  const { currentData: accounts, isFetching } = useGetAccountsQuery({
    org_id: authContext.organization.id,
    page: { page: 1, per_page: 20 },
    search_query: search || undefined,
  }, { skip: !authContext?.organization?.id });

  const me: OwnerOption = useMemo(() => ({ id: authContext.account.id, name: t('call_lists.owner.me') }), [authContext.account.id, t]);

  const options: OwnerOption[] = useMemo(() => [
    me,
    ...(accounts?.results ?? [])
      .filter((account) => !!account.id && account.id !== authContext.account.id)
      .map((account) => ({ id: account.id as string, name: `${account.first_name ?? ''} ${account.last_name ?? ''}`.trim() || account.email || '' })),
  ], [accounts, authContext.account.id, me]);

  const selected = options.find((option) => option.id === (value ?? me.id)) ?? (value ? { id: value, name: '' } : me);

  return (
    <Autocomplete
      size={size}
      disableClearable
      options={options}
      value={selected}
      loading={isFetching}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      filterOptions={(list) => list}
      onChange={(_event, option) => onChange(option.id === me.id ? null : option.id)}
      onInputChange={(_event, text, reason) => reason === 'input' && onSearch(text)}
      renderInput={(params) => <TextField {...params} label={label ?? t('call_lists.owner.label')} />}
      sx={{ minWidth: 200 }}
      data-testid="call-list-owner-picker"
    />
  );
}
